import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Text,
  Box,
  Icon,
  IconButton,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDown, Pin, BarChart3, Pencil, Trash2 } from "lucide-react";
import { IOperationRule } from "stores/data/operationRules/OperationRuleModel";
import { useTranslation } from "react-i18next";

interface DashboardSelectProps {
  operationRules: IOperationRule[];
  selectedId?: number;
  onChange: (id: number) => void;
  onPinToggle?: (item: IOperationRule) => void;
  onEdit?: (item: IOperationRule) => void;
  onDelete?: (item: IOperationRule) => void;
  showActions?: boolean;
  readOnly?: boolean;
  pinLoadingId?: number | null;
  hideIcon?: boolean;
}

export default function DashboardSelect({
  operationRules,
  selectedId,
  onChange,
  onPinToggle,
  onEdit,
  onDelete,
  showActions = false,
  readOnly = false,
  pinLoadingId = null,
  hideIcon = false,
}: DashboardSelectProps) {
  const selected = operationRules.find((r) => r.id === selectedId);

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightManager.eventManager",
  });

  const handlePinClick = (e: React.MouseEvent, item: IOperationRule) => {
    e.stopPropagation();
    if (onPinToggle && !readOnly) {
      onPinToggle(item);
    }
  };

  const handleEditClick = (e: React.MouseEvent, item: IOperationRule) => {
    e.stopPropagation();
    if (onEdit && !readOnly) {
      onEdit(item);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, item: IOperationRule) => {
    e.stopPropagation();
    if (onDelete && !readOnly) {
      onDelete(item);
    }
  };

  return (
    <Menu>
      <MenuButton
        as={Box}
        cursor="pointer"
        minW="250px"
        borderRadius="8px"
        px={2}
        py={1}
        _hover={{ bg: "gray.50" }}
        transition="background-color 0.2s"
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          w="100%"
          gap={4}
        >
          <Flex alignItems="center" gap={4} py={2}>
            {/* Show Pin icon if hideIcon is true and selected dashboard is favorite, otherwise show BarChart3 */}
            <Icon
              as={hideIcon && selected?.isFavorite ? Pin : BarChart3}
              boxSize={5}
              color="newBlue.500"
              fill={hideIcon && selected?.isFavorite ? "newBlue.500" : "none"}
            />
            <Flex alignItems="center" gap={2}>
              {/* Show pin icon next to name if selected dashboard is favorite and not using hideIcon */}
              {!hideIcon && selected?.isFavorite && (
                <Icon
                  as={Pin}
                  boxSize={4}
                  color="newBlue.500"
                  fill="newBlue.500"
                />
              )}
              <Box>
                <Text fontSize="16px" color="newBlue.500" lineHeight="normal">
                  {selected?.name || t("selectDashboard")}
                </Text>
                <Text fontSize="14px" color="gray.500" lineHeight="normal">
                  {t("selectedDashboard")}
                </Text>
              </Box>
            </Flex>
          </Flex>
          <Icon as={ChevronDown} boxSize={4} color="newBlue.500" ml={2} />
        </Flex>
      </MenuButton>

      <MenuList maxH="450px" overflowY="auto" minW="280px">
        {operationRules.map((value) => {
          const isSelected = value.id === selectedId;
          const isPinLoading = pinLoadingId === value.id;

          return (
            <MenuItem
              key={value.id}
              onClick={() => onChange(value.id)}
              bg={isSelected ? "gray.100" : "white"}
              color="gray.700"
              _hover={{ bg: "gray.50" }}
              fontSize="14px"
              py={2}
              px={3}
            >
              <Flex align="center" justify="space-between" w="100%" gap={2}>
                {/* Left side: Pin icon + Name */}
                <Flex align="center" gap={2} flex={1} minW={0}>
                  {/* Pin toggle button */}
                  {!readOnly && onPinToggle && (
                    <Tooltip
                      label={
                        value.isFavorite
                          ? t("unfavoriteDashboard")
                          : t("favoriteDashboard")
                      }
                      placement="top"
                      hasArrow
                    >
                      <IconButton
                        aria-label={
                          value.isFavorite
                            ? t("unfavoriteDashboard")
                            : t("favoriteDashboard")
                        }
                        icon={
                          isPinLoading ? (
                            <Spinner size="xs" color="newBlue.500" />
                          ) : (
                            <Icon
                              as={Pin}
                              boxSize={4}
                              color={
                                value.isFavorite ? "newBlue.500" : "gray.400"
                              }
                              fill={value.isFavorite ? "newBlue.500" : "none"}
                            />
                          )
                        }
                        variant="ghost"
                        size="xs"
                        minW="24px"
                        h="24px"
                        _hover={{ bg: "blue.50" }}
                        onClick={(e) => handlePinClick(e, value)}
                        isDisabled={isPinLoading}
                      />
                    </Tooltip>
                  )}
                  {/* Show static pin icon if readOnly and item is favorite */}
                  {readOnly && value.isFavorite && (
                    <Icon
                      as={Pin}
                      boxSize={4}
                      color="newBlue.500"
                      fill="newBlue.500"
                    />
                  )}
                  <Text fontWeight={400} noOfLines={1}>
                    {value.name}
                  </Text>
                </Flex>

                {/* Right side: Edit and Delete buttons (only when showActions) */}
                {showActions && !readOnly && (
                  <Flex align="center" gap={1}>
                    <Tooltip
                      label={t("editDashboard")}
                      placement="top"
                      hasArrow
                    >
                      <IconButton
                        aria-label={t("editDashboard")}
                        icon={<Icon as={Pencil} boxSize={4} color="gray.500" />}
                        variant="ghost"
                        size="xs"
                        minW="24px"
                        h="24px"
                        _hover={{ bg: "blue.50", color: "newBlue.500" }}
                        onClick={(e) => handleEditClick(e, value)}
                      />
                    </Tooltip>
                    <Tooltip
                      label={t("deleteDashboard")}
                      placement="top"
                      hasArrow
                    >
                      <IconButton
                        aria-label={t("deleteDashboard")}
                        icon={<Icon as={Trash2} boxSize={4} color="gray.500" />}
                        variant="ghost"
                        size="xs"
                        minW="24px"
                        h="24px"
                        _hover={{ bg: "red.50", color: "red.500" }}
                        onClick={(e) => handleDeleteClick(e, value)}
                      />
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}
